import _ from 'lodash'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { IconNames } from '@blueprintjs/icons'
import { Try } from 'shared-libs/helpers/utils'
import { Button, Icon } from '@blueprintjs/core'
import { formatExpression } from 'browser/components/atomic-elements/atoms/input/code-mirror-transforms'
import CodeMirror, { ReactCodeMirrorProps, ReactCodeMirrorRef } from '@uiw/react-codemirror'
import { solarizedLight } from '@uiw/codemirror-theme-solarized'
import { Diagnostic, forEachDiagnostic, linter } from '@codemirror/lint'
import { ViewUpdate, showPanel, EditorView } from '@codemirror/view'
import { Transaction, Text, Extension } from '@codemirror/state'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { javascript, javascriptLanguage, typescriptLanguage } from '@codemirror/lang-javascript'

import 'browser/components/atomic-elements/atoms/input/_code-mirror-input.scss'

class StatusLine extends React.Component {
  private statusLine: string | undefined = undefined

  public setStatusLine(statusLine: string) {
    this.statusLine = statusLine
    this.forceUpdate()
  }

  public render() {
    return <div className="code tr f6 pa1 bg-black-05">{this.statusLine}</div>
  }
}

interface ICodeMirrorInputProps extends ReactCodeMirrorProps {
  showStatusLine?: boolean
  language?: string
  /** Soft-wrap long lines instead of scrolling horizontally. Good for one-line munging expressions. */
  lineWrapping?: boolean
  /** Show explicit Format / Minify buttons (for templated-TS munging expressions). */
  showFormatButtons?: boolean
  /** Pretty-print for display only; the stored value is untouched until the user edits. */
  prettify?: boolean
  // value is a JSON object/array: shown pretty-printed, emitted parsed (invalid JSON is not emitted)
  isJsonValue?: boolean
}

interface ICodeMirrorInputState {
  /** current displayed value in the editor, could be formatted or non-formatted */
  value?: string
}

export class CodeMirrorInput extends React.Component<ICodeMirrorInputProps, ICodeMirrorInputState> {
  static defaultProps: Partial<ICodeMirrorInputProps> = {
    showStatusLine: true,
    prettify: false,
    showFormatButtons: false,
  }

  private editorRef = React.createRef<ReactCodeMirrorRef>()
  private statusLineRef = React.createRef<StatusLine>()
  private isUnmounted = false
  // Last value committed via onChange; distinguishes a user edit round-trip from an external change.
  private committedValue: string | undefined = undefined
  // isJsonValue: last typed text + its parse, so echoing our own change back doesn't reformat the doc
  private lastJsonText: string = undefined
  private lastJsonValue: any = undefined

  constructor(props: ICodeMirrorInputProps) {
    super(props)
    this.state = { value: props.isJsonValue ? this.getJsonText() : toEditorText(props.value) }
  }

  public componentDidMount() {
    this.updateValue(this.props.value)
  }

  public componentDidUpdate(prevProps: ICodeMirrorInputProps) {
    if (prevProps.value === this.props.value) {
      return
    }
    // User's own edit round-tripping back: keep it as-is (no reformat mid-typing).
    if (this.props.value === this.committedValue) {
      this.setState({ value: this.props.value })
      return
    }

    this.updateValue(this.props.value)
  }

  public componentWillUnmount() {
    this.isUnmounted = true
  }

  /** Pretty-print `value` for display; falls back to the raw value if it can't be parsed. */
  private updateValue(value: string | undefined) {
    if (this.props.isJsonValue) {
      return this.setState({ value: this.getJsonText() })
    }
    const text = toEditorText(value)
    if (!this.props.prettify) {
      return this.setState({ value: text })
    }

    void Promise.resolve(formatExpression(text))
      .catch(() => text)
      .then((formatted) => {
        // Formatting is async; drop the result if the value changed meanwhile (user started typing).
        if (!this.isUnmounted && this.props.value === value) {
          this.setState({ value: formatted })
        }
      })
  }

  shouldComponentUpdate(
    nextProps: Readonly<ICodeMirrorInputProps>,
    nextState: Readonly<ICodeMirrorInputState>
  ): boolean {
    return this.props.value !== nextProps.value || this.state.value !== nextState.value
  }

  public render() {
    return (
      <div className="c-section h-100 flex flex-column flex-grow">
        {this.renderEditor()}
        {this.renderStatusLine()}
      </div>
    )
  }

  private renderEditor() {
    const { language } = this.props
    const {
      value,
      showStatusLine,
      lineWrapping,
      showFormatButtons,
      prettify,
      isJsonValue,
      extensions: extraExtensions,
      ...props
    } = this.props

    const currValue = this.state.value
    const extensions = [
      ...getLanguageExtensions(language),
      ...(lineWrapping ? [EditorView.lineWrapping] : []),
      ...(showFormatButtons ? formatMinifyPanelExt() : []),
      ...(extraExtensions ?? []),
    ]
    return (
      <CodeMirror
        className="overflow-y-auto"
        theme={solarizedLight}
        style={{
          flexGrow: 1,
        }}
        basicSetup={{
          foldGutter: true,
          foldKeymap: true,
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightActiveLine: false,
        }}
        extensions={extensions}
        {...props}
        ref={this.editorRef}
        onChange={this.handleOnChange}
        onUpdate={(viewUpdate) => {
          if (showStatusLine) {
            // Set the computed status line text directly on the ref instead of
            // setting state here, due to a flaw in the React CodeMirror
            // wrapper. It seems to not behave nicely during a re-render.
            //
            // For example:
            //
            //  * CMD+F
            //  * type search text
            //  * press Enter or hit Next
            //
            // This triggers `onUpdate` with the new text selection state, and
            // if we set state here then CM seems to unexpectedly destroy its
            // search dialog during the re-render.
            const statusLine = this.statusLineRef.current
            if (!statusLine) {
              throw new TypeError('CodeMirrorInput status line is not mounted')
            }
            statusLine.setStatusLine(this.getStatusLineText(viewUpdate))
          }
        }}
        value={currValue}
      />
    )
  }

  private handleOnChange = (value: string, update: ViewUpdate) => {
    const { onChange, prettify, isJsonValue } = this.props
    if (prettify && value === this.state.value) {
      return
    }
    this.committedValue = value
    if (!isJsonValue) {
      onChange?.(value, update)
      return
    }
    let parsed
    try {
      parsed = _.isEmpty(_.trim(value)) ? undefined : JSON.parse(value)
    } catch {
      return
    }
    this.lastJsonText = value
    this.lastJsonValue = parsed
    onChange?.(parsed, update)
  }

  private getJsonText(): string {
    const value: unknown = this.props.value
    if (this.lastJsonText !== undefined && _.isEqual(value, this.lastJsonValue)) {
      return this.lastJsonText
    }
    return toEditorText(value)
  }

  private renderStatusLine() {
    const { showStatusLine } = this.props
    if (!showStatusLine) {
      return null
    }
    return <StatusLine ref={this.statusLineRef} />
  }

  private getStatusLineText(viewUpdate: ViewUpdate) {
    const anchor = this.getStatusLine(viewUpdate.state.doc, viewUpdate.state.selection.main.anchor)
    const head = this.getStatusLine(viewUpdate.state.doc, viewUpdate.state.selection.main.head)

    const line = this.formatRange(anchor.line, head.line)
    const col = this.formatRange(anchor.column, head.column)
    const offset = this.formatRange(anchor.offset, head.offset)
    return `Ln ${line}, Col ${col}, Pos ${offset}`
  }

  /* display two numbers as a range if they are unique */
  private formatRange: (from: number, to: number) => string = _.flow([
    _.concat,
    _.uniq,
    _.sortBy,
    _.partial(_.join, _, ' - '),
  ])

  private getStatusLine(doc: Text, pos: number) {
    const line = doc.lineAt(pos)
    return { line: line.number, column: pos - line.from, offset: pos }
  }

  public getEditor() {
    return this.editorRef.current
  }

  public setText(text: string | Text, opts: { isRemote?: boolean } = {}) {
    const { isRemote = false } = opts
    const view = this.editorRef.current?.view
    if (!view) {
      throw new TypeError('CodeMirrorInput editor view is not mounted')
    }
    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.length,
        insert: text,
      },
      annotations: [Transaction.remote.of(isRemote)],
    })
  }

  public hasError() {
    const state = this.editorRef.current?.state
    if (!state) {
      throw new TypeError('CodeMirrorInput editor state is not mounted')
    }
    let hasError = false
    forEachDiagnostic(state, (diag, from, to) => {
      if (diag.severity === 'error') {
        hasError = true
      }
    })
    return hasError
  }
}

// CodeMirror throws on any non-string value; stored data can be null or (legacy) an object.
function toEditorText(value: unknown): string {
  if (_.isNil(value)) {
    return ''
  }
  return _.isString(value) ? value : JSON.stringify(value, null, 2)
}

/** Position of the first Lezer error node, or null if the source parses cleanly. */
function firstSyntaxError(parser: any, src: string): { from: number; to: number } | null {
  let error: { from: number; to: number } | null = null
  parser.parse(src).iterate({
    enter: (node: any) => {
      if (!error && node.type.isError) {
        error = { from: node.from, to: node.to }
      }
    },
  })
  return error
}

/*
 * Syntax-only linter for JS/TS fields: parse with the Lezer grammar and flag the first error node.
 * `{{...}}` tokens are masked with an equal-length identifier so following offsets stay aligned.
 */
function makeSyntaxLinter(parser: any): Extension {
  return linter((view): Diagnostic[] => {
    const doc = view.state.doc.toString()
    if (doc.trim() === '') {
      return []
    }
    const masked = doc.replace(/\{\{[\s\S]*?\}\}/g, (token) => '_'.repeat(token.length))
    let error = firstSyntaxError(parser, masked)
    // A bare object literal parses as a block at statement position; re-check it parenthesized.
    if (error && masked.trim().startsWith('{') && !firstSyntaxError(parser, `(${masked})`)) {
      error = null
    }
    if (!error) {
      return []
    }
    const from = Math.min(error.from, Math.max(0, doc.length - 1))
    const to = Math.min(Math.max(error.to, from + 1), doc.length)
    return [{ from, to, severity: 'error', message: 'Syntax error' }]
  })
}

// An empty doc means "no value", not invalid JSON.
const parseJson = jsonParseLinter()
const jsonLinter = linter((view) => (view.state.doc.toString().trim() === '' ? [] : parseJson(view)))

const jsSyntaxLinter = makeSyntaxLinter(javascriptLanguage.parser)
const tsSyntaxLinter = makeSyntaxLinter(typescriptLanguage.parser)

function getLanguageExtensions(language?: string): Extension[] {
  switch (language) {
    case 'json':
      return [json(), jsonLinter]
    case 'javascript':
      return [javascript(), jsSyntaxLinter]
    case 'typescript':
      return [javascript({ typescript: true }), tsSyntaxLinter]
    default:
      return []
  }
}

/** Generic single-button overlay that runs a synchronous formatter (e.g. JSON pretty-print). */
export function formatButtonPanelExts(formatter: (input: string) => string): Extension[] {
  return [
    showPanel.of((view: EditorView) => {
      const dom = document.createElement('div')
      dom.className = 'relative'
      const root = createRoot(dom)
      root.render(
        <div className="absolute top-0 right-0 pa1">
          <Button onClick={() => handleFormatClick(view, formatter)}>
            <Icon icon={IconNames.CLEAN} />
          </Button>
        </div>
      )
      return { top: true, dom, destroy: () => root.unmount() }
    }),

    EditorView.baseTheme({
      // override panel z-index so CM lines don't prevent button clicks
      '.cm-panels': {
        zIndex: 1,
      },
    }),
  ]
}

function handleFormatClick(view: EditorView, formatter: (input: string) => string) {
  const formatted = Try(() => formatter(view.state.doc.toString()))
  if (formatted) {
    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.length,
        insert: formatted,
      },
    })
  }
}

/** Top-right overlay with Format and Minify buttons. Transforms live in `code-mirror-transforms.ts`. */
function formatMinifyPanelExt(): Extension[] {
  return [
    showPanel.of((view: EditorView) => {
      const dom = document.createElement('div')
      dom.className = 'relative'
      const root = createRoot(dom)
      root.render(
        <div className="absolute top-0 right-0 pa1 flex">
          <Button
            small
            minimal
            title="Format"
            onClick={() => replaceDoc(view, formatExpression(view.state.doc.toString()))}
          >
            <Icon icon={IconNames.CLEAN} />
          </Button>
        </div>
      )
      return { top: true, dom, destroy: () => root.unmount() }
    }),

    EditorView.baseTheme({
      // override panel z-index so CM lines don't prevent button clicks
      '.cm-panels': {
        zIndex: 1,
      },
    }),
  ]
}

/** Replace the whole document with the transform result, leaving the text unchanged if it throws. */
async function replaceDoc(view: EditorView, result: Promise<string>) {
  let text: string
  try {
    text = await result
  } catch {
    return
  }
  view.dispatch({
    changes: { from: 0, to: view.state.doc.length, insert: text },
  })
}
